import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { MatchedTerm, SearchRouteKey } from '../models/search-filter-spec.model';
import { AppStateResetService } from '../services/app-state-reset.service';

/** One live search-navigation broadcast — see {@link SearchArrivalRegistry.notifyLive}. */
export interface LiveArrival {
  route: SearchRouteKey;
  state: Record<string, unknown>;
}

/**
 * Read-once handoff for the chips a target page renders after a search navigation — mirrors
 * FilterRestoreRegistry's durable-flag pattern. Staged by SearchRoutingService immediately
 * before `router.navigate()`; consumed by the target page's `onDefault` hook.
 *
 * Also carries a SECOND, independent channel — {@link live$}/{@link notifyLive} — for the
 * case `SearchRoutingService.navigate()` cannot rely on a navigation at all: see that method's
 * own doc for why a same-URL search arrival needs a push channel instead of `router.navigate()`.
 */
@Injectable({ providedIn: 'root' })
export class SearchArrivalRegistry {
  private staged: MatchedTerm[] | null = null;

  /**
   * Broadcast channel behind {@link live$}/{@link notifyLive}. A plain `Subject`, not a
   * `BehaviorSubject` and not registered with {@link AppStateResetService}: unlike `staged`,
   * there is no state here for a stale value to leak from — a `Subject` holds nothing between
   * emissions, so a subscriber that mounts after a broadcast (a later page, or the next signed-in
   * user) simply never sees it. Nothing to clear.
   */
  private readonly live = new Subject<LiveArrival>();

  /** Live search-navigation broadcasts — see {@link notifyLive}. Pages subscribe, filtering by
      their own route key. */
  readonly live$: Observable<LiveArrival> = this.live.asObservable();

  constructor() {
    inject(AppStateResetService).register(() => { this.staged = null; });
  }

  stage(terms: MatchedTerm[]): void {
    this.staged = terms;
  }

  /** Read-once: returns the staged terms and clears them, or null if nothing was staged. */
  consume(): MatchedTerm[] | null {
    const terms = this.staged;
    this.staged = null;
    return terms;
  }

  /**
   * Broadcasts a search interpretation to whichever page is CURRENTLY mounted at
   * `arrival.route` — used when `router.navigate()` to that route would be a same-URL no-op
   * (Angular's default `onSameUrlNavigation: 'ignore'` silently drops a navigation to the page
   * the user is already on, and the default `RouteReuseStrategy` would keep re-running
   * `ngOnInit` even if it didn't — see `SearchRoutingService.navigate`). A mounted page
   * subscribes to {@link live$} and filters on `route` to pick up only its own arrivals.
   */
  notifyLive(arrival: LiveArrival): void {
    this.live.next(arrival);
  }
}
